"use client"

import * as React from "react"
import { format, parse } from "date-fns"
import { ro } from "date-fns/locale"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

/**
 * DatePicker component with Romanian locale
 * - Week starts on Monday
 * - Days and months in Romanian
 * - Date format: DD/MM/YYYY
 */
export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Selectați data",
  disabled = false,
  minDate,
  maxDate,
  className,
  ...props 
}) {
  const [open, setOpen] = React.useState(false)

  // Parse the value (expected format: YYYY-MM-DD from backend/form)
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    try {
      // Handle both Date object and string
      if (value instanceof Date) return value
      return parse(value, 'yyyy-MM-dd', new Date())
    } catch {
      return undefined
    }
  }, [value])

  // Format date for display (DD/MM/YYYY)
  const displayValue = React.useMemo(() => {
    if (!selectedDate || isNaN(selectedDate.getTime())) return ""
    return format(selectedDate, 'dd/MM/yyyy')
  }, [selectedDate])

  const handleSelect = (date) => {
    if (date) {
      // Return date in YYYY-MM-DD format for backend compatibility
      const formattedDate = format(date, 'yyyy-MM-dd')
      onChange(formattedDate)
    }
    setOpen(false)
  }

  // Convert min/max dates if provided as strings
  const minDateObj = React.useMemo(() => {
    if (!minDate) return undefined
    if (minDate instanceof Date) return minDate
    try {
      return parse(minDate, 'yyyy-MM-dd', new Date())
    } catch {
      return undefined
    }
  }, [minDate])

  const maxDateObj = React.useMemo(() => {
    if (!maxDate) return undefined
    if (maxDate instanceof Date) return maxDate
    try {
      return parse(maxDate, 'yyyy-MM-dd', new Date())
    } catch {
      return undefined
    }
  }, [maxDate])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0" 
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          locale={ro}
          weekStartsOn={1}
          disabled={(date) => {
            if (minDateObj && date < minDateObj) return true
            if (maxDateObj && date > maxDateObj) return true
            return false
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
